import React from 'react';
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Typography } from '@mui/material';

const getSentimentEmoji = (score) => {
  if (score >= 0.5) return '🟢'; // green for very positive
  if (score > 0) return '🔵'; // blue for positive
  if (score > -0.5) return '🟡'; // yellow for neutral/slight negative
  return '🔴'; // red for negative
};

const getSentimentColor = (score) => {
  if (score >= 0.5) return '#2e7d32'; // dark green
  if (score > 0) return '#1976d2'; // dark blue
  if (score > -0.5) return '#ed6c02'; // dark orange
  return '#e57373'; // lighter red
};

const getPriorityEmoji = (priority) => {
  switch (priority?.toLowerCase()) {
    case 'high':
      return '🔴'; // red
    case 'medium':
      return '🟡'; // yellow
    case 'low':
      return '🟢'; // green
    default:
      return '⚪️'; // white/grey
  }
};

const getPriorityColor = (priority) => {
  switch (priority?.toLowerCase()) {
    case 'high':
      return '#e57373'; // lighter red
    case 'medium':
      return 'rgba(23, 23, 22)'; // light orange with opacity
    case 'low':
      return '#2e7d32'; // dark green
    default:
      return 'rgba(189, 189, 189, 0.3)'; // light grey with opacity
  }
};

const AspectSentimentTable = ({ aspectData = [] }) => {
  // Sort aspects by sentiment score
  const sortedAspects = [...aspectData].sort((a, b) => b.sentiment_score - a.sentiment_score);

  return (
    <Paper sx={{ width: '100%', mb: 3, mt: 4 }}>
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Aspect</TableCell>
              <TableCell align="center" sx={{ minWidth: 120 }}>
                Sentiment
              </TableCell>
              <TableCell align="center" sx={{ minWidth: 140 }}>
                Priority
              </TableCell>
              <TableCell align="center">Mentions</TableCell>
              <TableCell>Feedback example</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedAspects.map((aspect) => (
              <TableRow key={aspect.aspect} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                <TableCell component="th" scope="row">
                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                    {aspect.aspect}
                  </Typography>
                </TableCell>
                <TableCell align="center">
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 'bold',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 0.5,
                      color: getSentimentColor(aspect.sentiment_score)
                    }}
                  >
                    {aspect.sentiment_score.toFixed(1)} {getSentimentEmoji(aspect.sentiment_score)}
                  </Typography>
                </TableCell>
                <TableCell align="center">
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 'medium',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 0.5,
                      textTransform: 'capitalize',
                      color: getPriorityColor(aspect.business_priority)
                    }}
                  >
                    {aspect.business_priority || 'N/A'} {getPriorityEmoji(aspect.business_priority)}
                  </Typography>
                </TableCell>
                <TableCell align="center">
                  <Typography variant="body2">{aspect.mentions_count || 'N/A'}</Typography>
                </TableCell>
                <TableCell>
                  <Typography
                    variant="body2"
                    sx={{
                      color: 'text.primary',
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis'
                    }}
                  >
                    {aspect.key_insight}
                  </Typography>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
};

export default AspectSentimentTable;
